import { type ReactNode, useState } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../../data/client';
import { Button, Popover } from '../../../ui';
import { FilterBuilder } from '../../filters/FilterBuilder';
import { isFilterableField, type RelationContext } from '../../filters/filterModel';
import type { ViewState } from '../views/viewState';
import { FilterTokenStrip } from './FilterTokenStrip';
import { filterTokens } from './filterTokens';
import type { RecordSegment } from './TableToolbar';
import { useTableControls } from './useTableControls';

/**
 * THE LIST'S HEADER ROW: ONE ROW OF CAPSULES, NO BOX (P4-4; spec/screens/list.md section 2
 * item 6, and section 7's "Apple does not put filter tokens or a filter bar above a table in
 * Finder or Mail; Mail's filters are capsule buttons in the list's header row, one row").
 *
 * WHAT WAS HERE BEFORE: two rows. The view switcher had a row of its own, and under it sat a
 * bordered toolbar strip holding an Active/Archived segmented control, a Filter button that
 * described the whole filter in one sentence, Sort, Columns and Group. That is a web
 * dashboard's construction: Mail puts the view's own controls in ONE line of capsules
 * directly above the list, with nothing drawn around them.
 *
 * WHAT IS IN THE ROW, in reading order:
 *
 *  - the view chip (the caller's `ViewSwitcher`, so the page keeps the view's own state);
 *  - ONE TOKEN PER ACTIVE FILTER, each with its own remove control (`filterTokens`), then
 *    "Add Filter" - so the filter is READABLE from the row instead of hiding behind a
 *    button that said "2 filters";
 *  - "Show Archived" as a token WHILE IT IS ON, which is what replaces the Active/Archived
 *    segmented control (spec item 6: "the Active and Archived control of today goes"). It is
 *    turned ON in the view options, because a segment nobody has switched on has nothing to
 *    show in a row about what narrows this list;
 *  - and nothing else.
 *
 * SORT, COLUMNS AND GROUP LEFT THIS ROW (wave 4, C12; `app/records/table/ViewOptions.tsx`).
 * They stood at the trailing end as three more capsules of the same family, and with the
 * detail pane open the row could not hold them: the four items outside the token strip took
 * 126 + 104 + 243 plus three gaps out of 611px, so the strip's window was narrower than one
 * token's own minimum and a filter drew its field name with no operand (P4-W3-14). They are
 * one toolbar item now, which is where `spec/screens/list.md` section 2 item 3 always had
 * them. What is left in the row is what this list SHOWS - which view, what narrows it, and
 * the way to narrow it further; what the view options hold is the list's SHAPE.
 *
 * THE ROW KEEPS AN INSET ON BOTH SIDES (wave 4, C2-8a, owner-77: "Group" flush on the pane
 * divider). Its leading edge stands in the window's own gutter and its trailing edge IS the
 * split's line, so a row that ends flush at its own box ends flush ON a drawn line. The inset
 * is `--list-pane-gutter`, which is the value the two rows around it already state against the
 * same line - `.list-footer`'s `padding-inline` and `.list-detail`'s, both `--cell-pad-x` - so
 * the pane's three chrome rows and the cells' own text now start on one vertical line and end
 * one inset short of the divider.
 */
export interface ListHeaderRowProps {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly relations: RelationContext;
  readonly state: ViewState;
  readonly onStateChange: (next: (current: ViewState) => ViewState) => void;
  /** The view chip. Rendered by the page, which owns the saved-view state it edits. */
  readonly viewSwitcher: ReactNode;
  readonly segment: RecordSegment;
  readonly onSegmentChange: (next: RecordSegment) => void;
}

export function ListHeaderRow({
  object,
  fields,
  relations,
  state,
  onStateChange,
  viewSwitcher,
  segment,
  onSegmentChange,
}: ListHeaderRowProps) {
  const { setFilter } = useTableControls({ object, fields, state, onStateChange });
  const [filterOpen, setFilterOpen] = useState(false);

  const tokens = filterTokens(state.filter, fields, relations);

  /**
   * The filter capsule disappears when nothing on this object can be filtered - a control
   * that opens an editor with no fields in it is a dead end. The related anchors count,
   * because "agencies with at least one deal" is a question about an object whose own
   * fields might all be currency.
   */
  const filterable =
    fields.some(isFilterableField) || (relations.anchorsOf(object.id) ?? []).length > 0;

  return (
    <div className="list-capsules px-(--list-pane-gutter)">
      {viewSwitcher}

      {/* THE TOKEN STRIP (wave 3, L-B3, and round 2's two halves of it): the row is ONE line
          at every width, the way Mail's filter bar is, and the strip is the only thing in it
          that gives. Inside a token the OPERAND truncates and the field's name and operator
          do not (`filterTokens.ts`); when the tokens cannot fit even at those minimums the
          strip PANS, and it is the only element on the row that moves - Add Filter and the
          view options sit outside it, which is what P4-W3-4 found wrong with the first
          scrolling build: the control that adds a filter scrolled out of sight.
          THE STRIP ITSELF MOVED OUT OF THIS FILE in wave 5 round 4 (B4): the phone's list
          shows the same row under its navigation bar, and one markup serves both. */}
      <FilterTokenStrip
        tokens={tokens}
        filter={state.filter}
        onFilterChange={setFilter}
        segment={segment}
        onSegmentChange={onSegmentChange}
      />

      {filterable && (
        <Popover
          open={filterOpen}
          onOpenChange={setFilterOpen}
          // WIDE, because a condition is a ROW (`ui-foundation.md` D11): field, operator,
          // value and remove read left to right, and four controls do not fit in 20rem.
          width="wide"
          trigger={
            <Button variant="push" size="sm" icon="plus">
              Add filter…
            </Button>
          }
        >
          {/* Mounted only while the popover is open, which is what seeds and drops the
              builder's draft - see the lifetime note in FilterBuilder. */}
          <FilterBuilder
            objectId={object.id}
            fields={fields}
            value={state.filter}
            onChange={setFilter}
            onDone={() => setFilterOpen(false)}
          />
        </Popover>
      )}
    </div>
  );
}
